
import { Injectable } from '@angular/core';
// FIX: Corrected import path for models.
// FIX: Added Store, PosDevice, and ActivityLog models to the import list.
import { AppUser, Permissions, PortalSettings, FeatureSettings, ReceiptSettings, Store, PosDevice, TaxSettings, LoyaltySettings, ClinicSettings, ActivityLog } from '../models/index';
import { MOCK_USERS } from '../data/mock-data';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

const USER_TABLE = 'users';

// FIX: Added mock data for stores and devices as creating new files is not allowed.
const MOCK_STORES: Store[] = [
  // CHANGE: Set default store to CLINIC to hide retail POS features
  { id: 'store-1', name: 'Main Branch', businessType: 'CLINIC', createdAt: new Date().toISOString() },
  { id: 'store-2', name: 'Second Branch', businessType: 'CLINIC', createdAt: new Date().toISOString() },
];

const MOCK_POS_DEVICES: PosDevice[] = [
  // FIX: Completed the `printerSettings` object to satisfy the `PrinterSettings` interface.
  { id: 'dev-1', name: 'Main Counter', storeId: 'store-1', status: 'active', createdAt: new Date().toISOString(), storeName: 'Main Branch', printerSettings: { model: 'Star TSP143IIILAN Ethernet', connectionType: 'ETHERNET', paperWidth: '80mm', autoPrint: true, advancedSettingsEnabled: false, advancedSettings: { printMode: 'GRAPHIC', printWidth: '72mm', printResolution: '203dpi', initialCommand: '', cutterCommand: '1D,56,42,00', drawerCommand: '1B,70,00,19,FA' } } },
  { id: 'dev-2', name: 'Back Counter', storeId: 'store-1', status: 'inactive', createdAt: new Date().toISOString(), storeName: 'Main Branch' },
  { id: 'dev-3', name: 'Front Desk', storeId: 'store-2', status: 'active', createdAt: new Date().toISOString(), storeName: 'Second Branch' },
];

@Injectable({ providedIn: 'root' })
export class UserService {
  private supabase: SupabaseClient | null = getSupabase();

  private toUserDB(user: Partial<AppUser>): any {
    return {
      // uid is the primary key, cannot be updated here
      name: user.name,
      // email is also an identifier and should not be updated
      role: user.role,
      status: user.status,
      pin: user.pin,
      signature_url: user.signatureUrl // NEW: Map signature
    };
  }
  
  async getUsers(): Promise<AppUser[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve(MOCK_USERS);
    }
    try {
      const { data, error } = await this.supabase.from(USER_TABLE).select('*');
      if (error) throw error;
      return (data || []).map(u => ({
          ...u, 
          storeId: u.store_id,
          signatureUrl: u.signature_url // NEW: Map signature back
      })) as AppUser[];
    } catch (e) {
      console.error("CRITICAL: Error getting users, returning empty array. Check RLS or connection.", e);
      return [];
    }
  }
  
  async checkStoreHasDoctors(): Promise<boolean> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve(true); // Mock mode assumes doctors exist
    }
    const { data, error } = await this.supabase.rpc('check_store_has_doctors');
    if (error) {
      console.error("Error checking for doctors:", error);
      return false;
    }
    return data as boolean;
  }

  async updateUser(updatedUser: AppUser): Promise<AppUser> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve(updatedUser);
    }
    const { uid, ...userData } = updatedUser;
    const dbPayload = this.toUserDB(userData);
    const { data, error } = await this.supabase.from(USER_TABLE).update(dbPayload).eq('uid', uid).select().single();
    if (error) throw error;
    return data as AppUser;
  }

  async deleteUser(uid: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve();
    }
    const { error } = await this.supabase.from(USER_TABLE).delete().eq('uid', uid);
    if (error) throw error;
  }

  // --- NEW: Upload Signature ---
  async uploadUserSignature(uid: string, signatureData: string): Promise<string> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return Promise.resolve("mock-signature-url");
    }

    try {
        // 1. Decode Base64
        const base64 = signatureData.includes('base64,') ? signatureData.split(',')[1] : signatureData;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });

        // 2. Upload to 'user-signatures' bucket
        // Path: {uid}/signature.png (Overwrite existing)
        const filePath = `${uid}/signature.png`;
        const { error: uploadError } = await this.supabase.storage
            .from('user-signatures')
            .upload(filePath, blob, { upsert: true, contentType: 'image/png' });

        if (uploadError) throw uploadError;

        // 3. Get Public URL
        const { data: publicUrlData } = this.supabase.storage
            .from('user-signatures')
            .getPublicUrl(filePath);
        
        const publicUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`; // Bust cache

        return publicUrl;
    } catch (e: any) {
        console.error("Signature Upload Error:", e);
        throw new Error(e.message || "Failed to upload signature");
    }
  }
  
  async createInvitation(email: string, role: AppUser['role']): Promise<string> {
    if (!isSupabaseConfigured() || !this.supabase) {
      throw new Error("Database not connected");
    }
    const { data, error } = await this.supabase.rpc('create_invitation', { p_email: email, p_role: role });
    if (error) throw error;
    return data.token;
  }

  async getInvitationDetails(token: string): Promise<{ email: string, role: string } | null> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return null;
    }
    const { data, error } = await this.supabase.rpc('get_invitation_details', { p_token: token });
    if (error) throw error;
    return data;
  }

  async getUserPermissions(): Promise<Permissions> {
    // --- Developer Mode Override ---
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.warn('%c🚀 DEV MODE ENABLED', 'color: #fff; background-color: #059669; padding: 2px 6px; border-radius: 4px; font-weight: bold;', 'Permissions have been overridden.');
      return {
        features: { "core_pos": true, "ai_order": true, "telemedicine": true, "loyalty": true, "reporting": true, "accounting": true, "nfc_cards": true, "automated_promptpay": true, "multi_store": true, "compliance_reports": true },
        limits: { "users": null, "medical_cases": null }
      };
    }

    if (!isSupabaseConfigured() || !this.supabase) {
      return { features: { "core_pos": true, "ai_order": true, "telemedicine": true, "loyalty": true, "reporting": true, "accounting": true, "compliance_reports": false }, limits: { "users": 10, "medical_cases": 100 } };
    }
    
    const { data, error } = await this.supabase.rpc('get_user_permissions');
    if (error) { console.error("Error fetching user permissions:", error); throw error; }
    return data as Permissions;
  }
  
  async sendTestAlert(message: string): Promise<{ success: boolean; message: string }> {
    if (!isSupabaseConfigured() || !this.supabase) {
      throw new Error("Database not connected.");
    }
    const { data, error } = await this.supabase.rpc('manual_test_alert', { p_message: message });
    if (error) throw error;
    return data;
  }

  // ... (Other settings methods remain the same) ...
  async getPortalSettings(): Promise<PortalSettings | null> { if (!this.supabase) return null; const { data, error } = await this.supabase.rpc('get_my_portal_settings'); if (error) throw error; return data; }
  async updatePortalSettings(settings: PortalSettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_portal_settings', { p_settings: settings }); if (error) throw error; }
  async getFeatureSettings(): Promise<FeatureSettings | null> { if (!this.supabase) return null; const { data, error } = await this.supabase.rpc('get_my_feature_settings'); if (error) throw error; return data; }
  async updateFeatureSettings(settings: FeatureSettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_feature_settings', { p_settings: settings }); if (error) throw error; }
  async getReceiptSettings(): Promise<ReceiptSettings | null> { if (!this.supabase) return null; const { data, error } = await this.supabase.rpc('get_my_receipt_settings'); if (error) throw error; return data; }
  async updateReceiptSettings(settings: ReceiptSettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_receipt_settings', { p_settings: settings }); if (error) throw error; }
  async getClinicSettings(): Promise<ClinicSettings | null> { if (!this.supabase) return { licenseNumber: '', operatingHours: '09:00 - 20:00', headerText: 'ใบรับรองแพทย์', enableTelemedAutoApprove: false, professionType: 'TRADITIONAL' }; const { data, error } = await this.supabase.rpc('get_my_clinic_settings'); if (error) throw error; return data; }
  async updateClinicSettings(settings: ClinicSettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_clinic_settings', { p_settings: settings }); if (error) throw error; }
  async getTaxSettings(): Promise<TaxSettings | null> { if (!this.supabase) return { enabled: true, name: 'VAT', rate: 7 }; const { data, error } = await this.supabase.rpc('get_my_tax_settings'); if (error) throw error; return data; }
  async updateTaxSettings(settings: TaxSettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_tax_settings', { p_settings: settings }); if (error) throw error; }
  async getLoyaltySettings(): Promise<LoyaltySettings | null> { if (!this.supabase) return { pointsPerBaht: 0.01 }; const { data, error } = await this.supabase.rpc('get_my_loyalty_settings'); if (error) throw error; return data; }
  async updateLoyaltySettings(settings: LoyaltySettings): Promise<void> { if (!this.supabase) return; const { error } = await this.supabase.rpc('update_my_loyalty_settings', { p_settings: settings }); if (error) throw error; }
  async ensureDataConsistency(): Promise<any> { if (!this.supabase) return; const { data, error } = await this.supabase.rpc('ensure_data_consistency'); if (error) throw error; return data; }

  // --- Activity Log Method ---
  async getActivityLogs(startDate?: string, endDate?: string, userId?: string, pageSize: number = 25, pageNumber: number = 1): Promise<{ logs: ActivityLog[], total_count: number }> {
      if (!isSupabaseConfigured() || !this.supabase) {
          // Mock Logs
          const mockLogs = [
              { id: '1', user_name: 'Dr. Somchai', action_type: 'PRESCRIPTION_APPROVE', description: 'Approved RX-2024-001', created_at: new Date().toISOString() },
              { id: '2', user_name: 'Jessica (Cashier)', action_type: 'LOGIN', description: 'Logged in with PIN', created_at: new Date(Date.now() - 3600000).toISOString() },
              { id: '3', user_name: 'Admin User', action_type: 'STOCK_ADJUST', description: 'Added 50 units to Tiger Eye', created_at: new Date(Date.now() - 7200000).toISOString() }
          ];
          return { logs: mockLogs.slice(0, pageSize), total_count: mockLogs.length };
      }
      const { data, error } = await this.supabase.rpc('get_store_activity_logs', {
          p_start_date: startDate,
          p_end_date: endDate,
          p_user_id: userId,
          p_page_size: pageSize,
          p_page_number: pageNumber
      });
      if (error) throw error;
      return data as { logs: ActivityLog[], total_count: number };
  }

  // --- Store & POS Device Methods ---
  private toPosDeviceDB(device: Partial<PosDevice>): any { return { id: device.id, name: device.name, store_id: device.storeId, status: device.status, printer_settings: device.printerSettings }; }
  async getStores(): Promise<Store[]> { if (!isSupabaseConfigured() || !this.supabase) { return Promise.resolve(MOCK_STORES); } const { data, error } = await this.supabase.from('stores').select('*'); if (error) throw error; return data as Store[]; }
  async getPosDevices(): Promise<PosDevice[]> { if (!isSupabaseConfigured() || !this.supabase) { return Promise.resolve(MOCK_POS_DEVICES); } const { data, error } = await this.supabase.from('pos_devices').select('*, store:stores(name)'); if (error) throw error; return (data || []).map((d: any) => ({ ...d, storeId: d.store_id, storeName: d.store?.name || 'Unknown Store', createdAt: d.created_at, printerSettings: d.printer_settings, })) as PosDevice[]; }
  async addPosDevice(deviceData: Omit<PosDevice, 'id' | 'createdAt'>): Promise<PosDevice> { if (!isSupabaseConfigured() || !this.supabase) { const newDevice: PosDevice = { ...deviceData, id: `mock-dev-${Date.now()}`, createdAt: new Date().toISOString() }; return Promise.resolve(newDevice); } const dbPayload = this.toPosDeviceDB(deviceData); delete (dbPayload as any).id; const { data, error } = await this.supabase.from('pos_devices').insert(dbPayload).select().single(); if (error) throw error; return data as PosDevice; }
  async updatePosDevice(updatedDevice: PosDevice): Promise<PosDevice> { if (!isSupabaseConfigured() || !this.supabase) { return Promise.resolve(updatedDevice); } const { id, ...deviceData } = updatedDevice; const dbPayload = this.toPosDeviceDB(deviceData); const { data, error } = await this.supabase.from('pos_devices').update(dbPayload).eq('id', id).select().single(); if (error) throw error; return data as PosDevice; }
  async deletePosDevice(deviceId: string): Promise<void> { if (!isSupabaseConfigured() || !this.supabase) { return Promise.resolve(); } const { error } = await this.supabase.from('pos_devices').delete().eq('id', deviceId); if (error) throw error; }
}
